import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyHoweMode({ brand }: ModeRendererProps) {
  // Wind-driven geometry; adaptive systems
  // Radial meshes, rotors, breathing forms
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.paper} />
      
      {/* Central rotor - Howe's kinetic sculpture style */}
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i / 12) * Math.PI * 2;
        const cx = 397;
        const cy = 280;
        const r1 = 60;
        const r2 = 140;
        const wave = Math.sin(i * 0.8) * 20;
        
        return (
          <g key={`rotor-blade-${i}`}>
            <path
              d={`M ${cx + Math.cos(angle) * r1},${cy + Math.sin(angle) * r1} 
                  Q ${cx + Math.cos(angle) * (r1 + r2) / 2 + wave},${cy + Math.sin(angle) * (r1 + r2) / 2} 
                  ${cx + Math.cos(angle) * r2},${cy + Math.sin(angle) * r2}`}
              fill="none"
              stroke={brand.aqua}
              strokeWidth="2"
              opacity={0.6}
            />
          </g>
        );
      })}
      
      {/* Radial mesh structure */}
      {Array.from({ length: 16 }).map((_, i) => {
        const angle = (i / 16) * Math.PI * 2;
        const cx = 397;
        const cy = 280;
        
        return (
          <line
            key={`radial-${i}`}
            x1={cx}
            y1={cy}
            x2={cx + Math.cos(angle) * 160}
            y2={cy + Math.sin(angle) * 160}
            stroke={brand.deep}
            strokeWidth="1"
            opacity={0.3}
          />
        );
      })}
      
      {/* Concentric circles - rotor rings */}
      {[40, 70, 100, 130, 160].map((r, i) => (
        <circle
          key={`ring-${i}`}
          cx="397"
          cy="280"
          r={r}
          fill="none"
          stroke={brand.violet}
          strokeWidth="1.5"
          opacity={0.4}
        />
      ))}
      
      {/* Breathing forms - pulsing elements */}
      {Array.from({ length: 6 }).map((_, i) => {
        const angle = (i / 6) * Math.PI * 2;
        const cx = 397 + Math.cos(angle) * 200;
        const cy = 280 + Math.sin(angle) * 200;
        const scale = 1 + Math.sin(i * 1.2) * 0.3;
        
        return (
          <g key={`breathing-${i}`}>
            {Array.from({ length: 5 }).map((_, j) => (
              <ellipse
                key={`pulse-${j}`}
                cx={cx}
                cy={cy}
                rx={(30 - j * 5) * scale}
                ry={(25 - j * 4) * scale}
                fill="none"
                stroke={brand.amber}
                strokeWidth="1"
                opacity={0.5 - j * 0.08}
              />
            ))}
          </g>
        );
      })}
      
      {/* Wind flow lines */}
      {Array.from({ length: 20 }).map((_, i) => {
        const y = 80 + i * 12;
        const flow = Math.sin(i * 0.5) * 30;
        
        const path = Array.from({ length: 50 }).map((_, j) => {
          const x = 60 + j * 14;
          const py = y + Math.sin(j * 0.2 + i * 0.3) * 15 + flow;
          return `${j === 0 ? "M" : "L"} ${x},${py}`;
        }).join(" ");
        
        return (
          <path
            key={`wind-${i}`}
            d={path}
            fill="none"
            stroke={brand.aqua}
            strokeWidth="0.5"
            opacity={0.3}
          />
        );
      })}
      
      {/* Secondary rotors - smaller scale */}
      {[
        { cx: 180, cy: 600 },
        { cx: 397, cy: 600 },
        { cx: 614, cy: 600 },
      ].map((pos, idx) => (
        <g key={`mini-rotor-${idx}`}>
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = (i / 8) * Math.PI * 2;
            const r1 = 30;
            const r2 = 70;
            
            return (
              <line
                key={`blade-${i}`}
                x1={pos.cx + Math.cos(angle) * r1}
                y1={pos.cy + Math.sin(angle) * r1}
                x2={pos.cx + Math.cos(angle) * r2}
                y2={pos.cy + Math.sin(angle) * r2}
                stroke={brand.violet}
                strokeWidth="1.5"
                opacity={0.5}
              />
            );
          })}
          {[25, 45, 65].map((r, i) => (
            <circle
              key={`mini-ring-${i}`}
              cx={pos.cx}
              cy={pos.cy}
              r={r}
              fill="none"
              stroke={brand.deep}
              strokeWidth="0.8"
              opacity={0.3}
            />
          ))}
        </g>
      ))}
      
      {/* Adaptive mesh - bottom section */}
      {Array.from({ length: 8 }).map((_, row) =>
        Array.from({ length: 12 }).map((_, col) => {
          const x = 80 + col * 54;
          const y = 780 + row * 40;
          const adapt = Math.sin(col * 0.4 + row * 0.5) * 8;
          
          return (
            <ellipse
              key={`mesh-${row}-${col}`}
              cx={x + adapt}
              cy={y}
              rx={18 + Math.abs(adapt)}
              ry={16 - Math.abs(adapt) * 0.5}
              fill="none"
              stroke={brand.aqua}
              strokeWidth="1"
              opacity={0.4}
            />
          );
        })
      )}
      
      {/* Vasarely optical pattern overlay */}
      {Array.from({ length: 10 }).map((_, i) => {
        const size = 200 - i * 18;
        const offset = i * 9;
        return (
          <circle
            key={`optical-${i}`}
            cx="397"
            cy="280"
            r={100 - i * 9}
            fill="none"
            stroke={brand.white}
            strokeWidth="0.5"
            opacity={0.15}
          />
        );
      })}
    </g>
  );
}
